/**
 * Objective wayfinding (q-player-names-and-wayfinding; docs/design/ui-hud-wayfinding.md).
 *
 * Owner phone playtest, 2026-10-24: "we refer to the crafting table being in the west but do not
 * give the player a compass nor any other indicator on which the crafting table is". The cause:
 * a quest objective named a target (a resource, an enemy, an NPC, a crafting station) and nothing
 * on screen pointed at it — the minimap was fixed to the world axes (agreeing with the view only
 * when the camera faced −z) with no objective pin, no station position reached the client at
 * all, and the dialogue gave a cardinal direction nothing on screen could resolve.
 *
 * This module is the pure half: which objective the HUD tracks, where its target stands, and
 * where that target is on screen for the current camera yaw. The HUD's wayfinder arrow and the
 * heading-up minimap pin (`Hud.tsx`) draw what it returns. Display only — the server still
 * judges every gather, talk, craft and kill.
 */

import {
  isReadyToTurnIn,
  markerKindFor,
  nearestArea,
  searchAreas,
  type ClientContentPack,
  type ObjectiveMarkerKind,
  type SearchArea,
} from '@great-cloud/shared/client';

import { craftStationOf, enemyObject, nameOf, stationLabel } from './playerText.js';

/** A placed crafting station (a settlement piece that declares `station`). */
export interface StationAt {
  station: string;
  x: number;
  z: number;
}

let stationsByBiome = new Map<string, StationAt[]>();

/** Register every biome's placed stations from the loaded pack (App, on each pack sync). */
export function setWayfindingContent(pack: ClientContentPack | null): void {
  const next = new Map<string, StationAt[]>();
  for (const [biomeId, def] of Object.entries(pack?.categories.biomes ?? {})) {
    next.set(biomeId, placedStations(def['generation']));
  }
  stationsByBiome = next;
}

/** The stations a biome's `generation.settlements` place (settlement origin + piece offset). */
export function placedStations(generation: unknown): StationAt[] {
  const out: StationAt[] = [];
  const settlements = (generation as { settlements?: unknown } | undefined)?.settlements;
  if (!Array.isArray(settlements)) return out;
  for (const st of settlements as {
    position?: { x?: number; z?: number };
    pieces?: { x?: number; z?: number; station?: unknown }[];
  }[]) {
    const ox = st.position?.x ?? 0;
    const oz = st.position?.z ?? 0;
    for (const p of st.pieces ?? []) {
      if (typeof p.station !== 'string') continue;
      out.push({ station: p.station, x: ox + (p.x ?? 0), z: oz + (p.z ?? 0) });
    }
  }
  return out;
}

export function stationsIn(biomeId: string | undefined): StationAt[] {
  return biomeId ? (stationsByBiome.get(biomeId) ?? []) : [];
}

// ---------------------------------------------------------------------------
// Which objective is tracked
// ---------------------------------------------------------------------------

export interface QuestLike {
  questId: string;
  name: string;
  status: string;
  turnInNpc: string | null;
  objectives: {
    verb: string;
    target: string | null;
    item: string | null;
    count: number;
    progress: number;
    done: boolean;
  }[];
}

/** What the HUD points at: an unfinished objective, or the quest's turn-in. */
export type Tracked =
  | { kind: 'objective'; quest: QuestLike; objective: QuestLike['objectives'][number] }
  | { kind: 'turnIn'; quest: QuestLike; npcId: string };

/**
 * The tracked step: the first quest still in play (log order — the order the server keeps),
 * its first unfinished objective; a quest whose objectives are all done points at its turn-in.
 * `null` when nothing is in play.
 */
export function trackedObjective(quests: readonly QuestLike[]): Tracked | null {
  // AD-867: a quest ready to hand in is the most useful thing to point at — it goes first. Ready
  // includes a quest whose only open objectives are talks to its turn-in NPC (isReadyToTurnIn).
  const ready = quests.find((q) => isReadyToTurnIn(q));
  if (ready?.turnInNpc) return { kind: 'turnIn', quest: ready, npcId: ready.turnInNpc };
  for (const quest of quests) {
    if (quest.status === 'turned_in') continue;
    const next = quest.objectives.find((o) => !o.done);
    if (quest.status === 'active' && next) return { kind: 'objective', quest, objective: next };
    if (quest.turnInNpc) return { kind: 'turnIn', quest, npcId: quest.turnInNpc };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Where its target stands
// ---------------------------------------------------------------------------

export interface WorldView {
  self: { x: number; z: number } | null;
  npcs: readonly { id: string; name: string; x: number; z: number }[];
  nodes: readonly { resource: string; x: number; z: number; depleted: boolean }[];
  /** Live entities (enemies carry their content NAME on the wire, not their id). */
  entities: Iterable<{ kind?: string; name?: string; x?: number; z?: number; health?: number }>;
  stations: readonly StationAt[];
  /** The zone's exits (`to` = the biome each leads to) — a `visit` objective points at one. */
  transitions?: readonly { to: string; x: number; z: number }[];
  /** Where each enemy kind lives (join ack `enemyAreas`, AD-867) — a `kill` objective's area. */
  enemyAreas?: readonly ({ enemyId: string } & SearchArea)[];
}

export interface Waypoint {
  /** What the dial says: "Alchemy Table", "Warden Sel", "Fenroot Bark". */
  label: string;
  x: number;
  z: number;
  /**
   * AD-867: `pin` = an exact place (an NPC, a station, an exit); `area` = a circle to search
   * (`radiusM`) around where a resource grows or a creature lives — never the node or creature.
   * Decided by the objective's verb (`OBJECTIVE_MARKER`), never by the quest.
   */
  kind: ObjectiveMarkerKind;
  radiusM?: number;
}

function nearest<T extends { x: number; z: number }>(
  self: { x: number; z: number } | null,
  list: readonly T[],
): T | undefined {
  if (list.length === 0) return undefined;
  if (!self) return list[0];
  let best = list[0];
  let bestD = Infinity;
  for (const p of list) {
    const d = Math.hypot(p.x - self.x, p.z - self.z);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

/**
 * The world point a tracked step sends the player to, or null when this zone has nothing to
 * point at (an objective in another zone; an enemy out of sight — the label still shows).
 */
export function waypointFor(tracked: Tracked, world: WorldView): Waypoint | null {
  const npcAt = (id: string): Waypoint | null => {
    const npc = world.npcs.find((n) => n.id === id);
    return npc ? { label: npc.name, x: npc.x, z: npc.z, kind: 'pin' } : null;
  };
  if (tracked.kind === 'turnIn') return npcAt(tracked.npcId);
  const o = tracked.objective;
  const wp = targetOf(o, world, npcAt);
  // The verb decides pin vs area (AD-867); a resolver that disagrees is a defect the content
  // check (wayfinding.test.ts) catches — here the table wins so the HUD never over-reveals.
  if (wp && markerKindFor(o.verb) === 'area' && wp.kind !== 'area') return null;
  return wp;
}

function areaWaypoint(label: string, area: SearchArea | null): Waypoint | null {
  return area ? { label, x: area.x, z: area.z, kind: 'area', radiusM: area.radiusM } : null;
}

function targetOf(
  o: QuestLike['objectives'][number],
  world: WorldView,
  npcAt: (id: string) => Waypoint | null,
): Waypoint | null {
  switch (o.verb) {
    case 'talk':
    case 'deliver':
      return o.target ? npcAt(o.target) : null;
    case 'gather': {
      // Where it grows: the nodes' search areas (live ones when any are up), never the node.
      const of = world.nodes.filter((n) => n.resource === o.target);
      const live = of.filter((n) => !n.depleted);
      const area = nearestArea(world.self, searchAreas(live.length > 0 ? live : of));
      return areaWaypoint(nameOf('resources', o.target), area);
    }
    case 'craft': {
      const station = craftStationOf(o.target);
      if (!station) return null;
      const at = nearest(
        world.self,
        world.stations.filter((s) => s.station === station),
      );
      return at ? { label: stationLabel(station), x: at.x, z: at.z, kind: 'pin' } : null;
    }
    case 'kill': {
      // Where it lives: the zone's spawn areas for the kind; else the ones in sight, as an area.
      const homes = (world.enemyAreas ?? []).filter((a) => a.enemyId === o.target);
      if (homes.length > 0)
        return areaWaypoint(enemyObject(o.target), nearestArea(world.self, homes));
      const name = nameOf('enemies', o.target);
      const alive: { x: number; z: number }[] = [];
      for (const e of world.entities) {
        if (e.kind !== 'enemy' || e.name !== name || e.x == null || e.z == null) continue;
        if (e.health !== undefined && e.health <= 0) continue;
        alive.push({ x: e.x, z: e.z });
      }
      return areaWaypoint(enemyObject(o.target), nearestArea(world.self, searchAreas(alive)));
    }
    case 'visit': {
      const exit = nearest(
        world.self,
        (world.transitions ?? []).filter((t) => t.to === o.target),
      );
      return exit ? { label: nameOf('biomes', o.target), x: exit.x, z: exit.z, kind: 'pin' } : null;
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Screen geometry
// ---------------------------------------------------------------------------

/**
 * A world offset (dx, dz) as the follow camera at `cameraYaw` shows it: metres to the right of
 * and up the screen from the player — the same mapping WASD uses (`cameraRelativeMove`,
 * input.ts): screen-up is world (sin yaw, cos yaw), screen-right is (−cos yaw, sin yaw).
 *
 * Why the HUD carries NO cardinal letters (docs/design/ui-hud-wayfinding.md §3): with the
 * documented convention (+x east, +z north) in a right-handed renderer, a player looking north
 * sees east on their LEFT — the world as drawn is a mirror image of the convention, so an "N"
 * on a dial would put "W" where a player expects "E". Directions are given by the arrow, the
 * heading-up minimap and landmarks until that convention is ruled on.
 */
export function screenOffset(
  dx: number,
  dz: number,
  cameraYaw: number,
): { right: number; up: number } {
  return {
    up: dx * Math.sin(cameraYaw) + dz * Math.cos(cameraYaw),
    right: -dx * Math.cos(cameraYaw) + dz * Math.sin(cameraYaw),
  };
}

/** The screen angle (degrees, clockwise from straight up) of a world offset (dx, dz). */
export function screenAngleDeg(dx: number, dz: number, cameraYaw: number): number {
  const { right, up } = screenOffset(dx, dz, cameraYaw);
  const deg = (Math.atan2(right, up) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

/** The dial's distance: rounded metres once the player's position is known. */
/** For an area (AD-867) the distance is to its EDGE: 0 once the player is inside it. */
export function waypointDistanceM(
  self: { x: number; z: number } | null,
  wp: { x: number; z: number; radiusM?: number },
): number | null {
  if (!self) return null;
  return Math.round(Math.max(0, Math.hypot(wp.x - self.x, wp.z - self.z) - (wp.radiusM ?? 0)));
}

/** Within this distance the dial says "here" instead of an arrow (the player has arrived). */
export const WAYPOINT_ARRIVED_M = 3;

/**
 * The minimap's projection: HEADING-UP around the player — up the map is up the screen, right
 * is right — with ONE scale for both axes, so the map and the view always agree. The pre-fix map
 * was fixed to the world axes with +z drawn DOWN and x and z stretched independently, so it
 * agreed with the view only when the camera happened to face −z, and bearings on it were skewed.
 */
export function minimapProjector(
  points: readonly { x: number; z: number }[],
  centre: { x: number; z: number },
  cameraYaw: number,
  sizePx: number,
  marginM = 3,
  padPx = 2,
): (x: number, z: number) => [number, number] {
  let reach = 1;
  for (const p of points) {
    const o = screenOffset(p.x - centre.x, p.z - centre.z, cameraYaw);
    reach = Math.max(reach, Math.abs(o.right), Math.abs(o.up));
  }
  const half = (sizePx - 2 * padPx) / 2;
  const scale = half / (reach + marginM);
  const mid = padPx + half;
  return (x, z) => {
    const o = screenOffset(x - centre.x, z - centre.z, cameraYaw);
    return [mid + o.right * scale, mid - o.up * scale];
  };
}
